// CUSTOM VUE GLOBAL MIXIN TO ADD POSTHOG TO ALL COMPONENTS

// POSTHOG properties can only be string, number, boolean, or null (no arrays or complex objects it seems)
// so we will make keys for tenant-specific things by joining {subdomain}_{attribute}: {value}

const subdomainProperty = (propertyName) => {
    if (typeof propertyName !== 'string') {
        console.warn(`in subdomainProperty propertyName is not a string`, propertyName);
        return; 
    }
    const subdomain = window?.currentUserInfo?.tenant_subdomain;
    const sep = '__'  // use dunder so its easier to parse, since you'll have things with single underscore
    return (!!subdomain && !!propertyName) ? (subdomain + sep + propertyName) : '';
};

const setIf = (obj, key, value) => {
    // helper for using below - only set key on obj if value is NOT undefined or null
    // must do it like this so we can set things that are explicitly false or 0
    // obviously can't use this to clear out existing values and set to null... but you could add another arg to do that
    if (value !== null && value !== undefined) {
        obj[key] = value;
    }
};

const posthogMixin = {
    computed: {
        $posthog() {
            return window.posthog;  // we load posthog using script tag in <head> - this will be undefined if its not there
        },
    },
    // add some helper methods for doing things with posthog (identify, add event, etc.) that handles posthog not being on the page
    methods: {
        $_posthogIdentify(setProps, setOnceProps) {
            // DRY helper to use in use-case-specific methods
            const userInfo = window?.currentUserInfo;
            const userId = userInfo ? userInfo['user_id'] : '';  // you use userId (CoreUser) to track in Posthog for now
            if (userInfo && userId && this.$posthog) {
                // should we check if we have either setProps or setOnceProps?  no, not for now... because just a lone identify call might still be useful, at least for aliasing tracking IDs
                this.$posthog.identify(userId, setProps, setOnceProps);  // make the call
            } else {
                console.warn('Tried to call posthog identify but missing user info or $posthog');
            }
        },
        $posthogIdentify() {
            // call this from the main app dashboard and/or after login to stash things you want to regularly update
            // NOTE - in posthog.init() you have 'loaded' callback which makes identity call also, with minimal other data being set 
            //      - that is good for identifying users to make sure we have all the right IDs connected
            //      - then call this occasionally (on dashboard pages, for example) to make sure all key info is up to date
            
            // OR... maybe you should add all the tenant-specific stuff in an event instead of on user/person?
            // if there is a limit to number of properties, then yes, you should
            
            const userInfo = window?.currentUserInfo;
            const subdomain = userInfo.tenant_subdomain;
            if (!userInfo || !subdomain) {
                return;
            }

            const setData = {};

            // set some properties for subdomain / tenant_id -- but make sure they are unique so not overwritten, and easy for querying
            // also make sure they don't collide with other properties - so should not just be the subdomain... since that could be same as some property
            const subdomainProp = `subdomain_${subdomain}`;
            setData[subdomainProp] = true;  // just set true for easy querying -- although we probably don't need this if we have subdomain appended to all tenant-specific properties...
            const tenant_id = userInfo.tenant_id;  // do the same with tenant_id (since its a string) - for easy querying, just check for any user with that tenant id = true
            if (tenant_id) {
                const tenantIdProperty = `tenant_${tenant_id}`;
                setData[tenantIdProperty] = true;
            }
            setIf(setData, 'name', userInfo.full_name);
            setIf(setData, 'email', userInfo.user_email);  // use USER email for this 
            setIf(setData, subdomainProperty('tu_id'), userInfo.tenant_user_id);
            setIf(setData, subdomainProperty('tu_email'), userInfo.tu_email);
            setIf(setData, subdomainProperty('role_slug'), userInfo.tu_role_slug);
            setIf(setData, subdomainProperty('is_internal'), userInfo.is_internal);
            setIf(setData, subdomainProperty('company_id'), userInfo.company_id);
            setIf(setData, subdomainProperty('company_name'), userInfo.company_name);

            if (Object.keys(setData).length) {
                // console.log('sending to the hedgehog');
                this.$_posthogIdentify(setData);  // only calling set, not set_once... set other properties with set_once in OTHER places
            }
        },
        $posthogCapture(event_name, eventProps) {
            // calls posthog.capture('some_event', {someProp: 'value1', anotherProp: 'value2'});
            // event_name should be a string, eventProps should be an object with any props you want to pass in
            if (this.$posthog) {
                this.$posthog.capture(event_name, eventProps);
            }
        }
    }
}


export default posthogMixin;